import type { CustomNode, NodeStruct } from '@/types/node-types';
import type { CustomEdge } from '@/types/edge-types';
import {
  getSwitchConfig,
  type ExpressionAst,
  type SwitchCase,
} from '@/pages/workflow/helper/functional-control-flow';
import {
  CLASSIFY_DECIDE_NODE_ID,
  DECISION_PRESET_SCAFFOLDS,
} from '@/pages/workflow/component/custom-node/generic-node/classify-decision-presets';
import { describeLoops, nodeLabel } from '@/pages/workflow/helper/repeat-loop';
import { VALUE_ROOT } from '@/pages/workflow/helper/switch-condition-builder';

/**
 * Classify / Decide as a configurable judgment: a preset, a label set and
 * criteria, plus the two things downstream that depend on the labels (a
 * Switch's cases, a Repeat Until's exit condition). Nothing here writes to
 * the store; the panel calls these and commits through the normal actions,
 * and every downstream update is an explicit button, never silent.
 */

export { CLASSIFY_DECIDE_NODE_ID };

export const CLASSIFY_LABEL_RE = /^[a-z][a-z0-9_]{0,63}$/;
export const CLASSIFY_MIN_LABELS = 2;
export const CLASSIFY_MAX_LABELS = 16;

/** Fields the Classify panel owns (the renderer skips them). */
export const CLASSIFY_PANEL_FIELD_IDS: ReadonlySet<string> = new Set([
  'decision_preset',
  'decision_labels',
  'criteria',
  'uncertain_label',
  'uncertain_threshold',
]);

export type PresetId = 'binary_gate' | 'triage' | 'custom';

export interface PresetCard {
  id: PresetId;
  title: string;
  summary: string;
  labels: string[];
}

export const PRESET_CARDS: PresetCard[] = [
  {
    id: 'binary_gate',
    title: 'Yes / no gate',
    summary: 'Does the subject meet the requirements?',
    labels: DECISION_PRESET_SCAFFOLDS.binary_gate.labels,
  },
  {
    id: 'triage',
    title: 'Triage',
    summary: 'Sort into urgent, routine or ignore.',
    labels: DECISION_PRESET_SCAFFOLDS.triage.labels,
  },
  {
    id: 'custom',
    title: 'Your own labels',
    summary: 'Name the buckets and say what each means.',
    labels: [],
  },
];

export interface ClassifyConfig {
  preset: PresetId;
  labels: string[];
  criteria: string;
  uncertainLabel: string | null;
  uncertainThreshold: number | null;
}

const paramValue = (struct: NodeStruct | undefined, fieldId: string): unknown =>
  struct?.params?.find((param) => param.field_id === fieldId)?.value;

const paramDefault = (
  struct: NodeStruct | undefined,
  fieldId: string,
): unknown =>
  struct?.params?.find((param) => param.field_id === fieldId)?.default;

const asLabels = (value: unknown): string[] | null => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) return asLabels(parsed);
    } catch {
      // A plain comma-separated list typed by hand.
      return value
        .split(/[,\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return null;
};

export const getClassifyConfig = (
  struct: NodeStruct | undefined,
): ClassifyConfig => {
  const storedPreset = paramValue(struct, 'decision_preset');
  const rawPreset =
    storedPreset === undefined || storedPreset === null || storedPreset === ''
      ? paramDefault(struct, 'decision_preset')
      : storedPreset;
  const preset: PresetId =
    rawPreset === 'binary_gate' || rawPreset === 'triage'
      ? rawPreset
      : 'custom';
  const labels =
    asLabels(paramValue(struct, 'decision_labels')) ??
    asLabels(paramDefault(struct, 'decision_labels')) ??
    [];
  const criteria = paramValue(struct, 'criteria');
  const uncertainLabel = paramValue(struct, 'uncertain_label');
  const threshold = paramValue(struct, 'uncertain_threshold');
  return {
    preset,
    labels,
    criteria: typeof criteria === 'string' ? criteria : '',
    uncertainLabel:
      typeof uncertainLabel === 'string' && uncertainLabel.trim()
        ? uncertainLabel.trim()
        : null,
    uncertainThreshold:
      typeof threshold === 'number' && Number.isFinite(threshold)
        ? threshold
        : null,
  };
};

/** The preset whose labels exactly match the current set, else `custom`. */
export const presetForLabels = (labels: readonly string[]): PresetId => {
  for (const card of PRESET_CARDS) {
    if (
      card.id !== 'custom' &&
      card.labels.length === labels.length &&
      card.labels.every((label, index) => label === labels[index])
    ) {
      return card.id;
    }
  }
  return 'custom';
};

export interface ClassifyProblem {
  field: 'labels' | 'criteria' | 'uncertain';
  message: string;
}

export const normalizeLabel = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

export const validateClassifyConfig = (
  config: ClassifyConfig,
): ClassifyProblem[] => {
  const problems: ClassifyProblem[] = [];
  if (config.labels.length < CLASSIFY_MIN_LABELS) {
    problems.push({
      field: 'labels',
      message: `Give the decision at least ${CLASSIFY_MIN_LABELS} labels.`,
    });
  }
  if (config.labels.length > CLASSIFY_MAX_LABELS) {
    problems.push({
      field: 'labels',
      message: `At most ${CLASSIFY_MAX_LABELS} labels; fold the rare ones into one.`,
    });
  }
  const bad = config.labels.filter((label) => !CLASSIFY_LABEL_RE.test(label));
  if (bad.length) {
    problems.push({
      field: 'labels',
      message: `Labels are lower_snake_case words: ${bad.map((label) => `"${label}"`).join(', ')} ${bad.length === 1 ? 'is' : 'are'} not.`,
    });
  }
  const seen = new Set<string>();
  for (const label of config.labels) {
    if (seen.has(label)) {
      problems.push({
        field: 'labels',
        message: `"${label}" appears twice; labels must be unique.`,
      });
      break;
    }
    seen.add(label);
  }
  if (!config.criteria.trim()) {
    problems.push({
      field: 'criteria',
      message: 'Say how to decide: what makes the subject each label?',
    });
  }
  if (config.uncertainLabel && !config.labels.includes(config.uncertainLabel)) {
    problems.push({
      field: 'uncertain',
      message: `The fallback label "${config.uncertainLabel}" must be one of the labels.`,
    });
  }
  return problems;
};

// --- what depends on the labels ---------------------------------------------

export interface DownstreamSwitch {
  switchId: string;
  switchName: string;
  /** The path a case compares, relative to the Switch's `value` root. */
  decisionPath: Array<string | number>;
  /** Via a Validate node (the judgment-gate shape) or directly. */
  via: 'direct' | 'validate';
  currentLabels: string[];
}

export interface DownstreamLoop {
  carrierId: string;
  carrierName: string;
}

const edgeSource = (edge: CustomEdge): string =>
  edge.source_node_id || edge.source;
const edgeTarget = (edge: CustomEdge): string =>
  edge.target_node_id || edge.target;

const portName = (
  node: CustomNode | undefined,
  portId: string | null | undefined,
): string | null =>
  node?.data?.struct?.output_ports?.find((port) => port.id === portId)?.name ??
  null;

/**
 * The path a Switch case should compare for a decision coming from this
 * Classify node: the `decision` port carries the bare label, the `result`
 * port the envelope (`decision` inside), and a Validate in between wraps it
 * once more (the judgment-gate stamp's `value.value.decision`).
 */
export const inferDecisionPath = (
  sourcePort: string | null,
  via: 'direct' | 'validate',
): Array<string | number> => {
  const inner =
    sourcePort === 'decision'
      ? []
      : sourcePort === 'metadata'
        ? []
        : ['decision'];
  return via === 'validate' ? ['value', ...inner] : inner;
};

/** Switch nodes fed by this Classify node, directly or through one Validate. */
export const findDownstreamSwitches = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  classifyId: string,
): DownstreamSwitch[] => {
  const classify = nodes.find((node) => node.id === classifyId);
  const found: DownstreamSwitch[] = [];
  const seen = new Set<string>();
  const add = (
    switchNode: CustomNode,
    sourcePort: string | null,
    via: 'direct' | 'validate',
  ) => {
    if (seen.has(switchNode.id)) return;
    seen.add(switchNode.id);
    const config = getSwitchConfig(switchNode.data.struct);
    const existingPath = pathUsedByCases(config.cases);
    found.push({
      switchId: switchNode.id,
      switchName: nodeLabel(switchNode),
      decisionPath: existingPath ?? inferDecisionPath(sourcePort, via),
      via,
      currentLabels: config.cases.map((item) => item.label),
    });
  };
  for (const edge of edges) {
    if (edgeSource(edge) !== classifyId) continue;
    const target = nodes.find((node) => node.id === edgeTarget(edge));
    if (!target) continue;
    const sourcePort = portName(
      classify,
      edge.sourceHandle ?? edge.source_port_id,
    );
    if (target.data.struct.node_id === 'switch_node') {
      add(target, sourcePort, 'direct');
    } else if (target.data.struct.node_id === 'validate_node') {
      for (const next of edges) {
        if (edgeSource(next) !== target.id) continue;
        const after = nodes.find((node) => node.id === edgeTarget(next));
        if (after?.data.struct.node_id === 'switch_node') {
          add(after, sourcePort, 'validate');
        }
      }
    }
  }
  return found;
};

/** Repeat Until loops where this Classify node is the judge. */
export const findLoopsJudgedBy = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  classifyId: string,
): DownstreamLoop[] =>
  describeLoops(nodes, edges)
    .filter((loop) => loop.evaluatorId === classifyId)
    .map((loop) => ({
      carrierId: loop.carrierId,
      carrierName: nodeLabel(loop.carrier),
    }));

// --- syncing a Switch's cases to the labels ---------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** `{op:'eq', left:{op:'path',path:[value,...]}, right:{op:'literal',value:<label>}}` → the path after the root. */
const decisionPathOfCase = (
  when: ExpressionAst,
): Array<string | number> | null => {
  if (!isRecord(when) || when.op !== 'eq') return null;
  const left = when.left;
  const right = when.right;
  if (!isRecord(left) || left.op !== 'path' || !Array.isArray(left.path)) {
    return null;
  }
  if (!isRecord(right) || right.op !== 'literal') return null;
  const path = left.path as Array<string | number>;
  if (path[0] !== VALUE_ROOT) return null;
  return path.slice(1);
};

/** The decision path the Switch's cases already compare, when they agree. */
export const pathUsedByCases = (
  cases: readonly SwitchCase[],
): Array<string | number> | null => {
  let path: Array<string | number> | null = null;
  for (const item of cases) {
    const candidate = decisionPathOfCase(item.when);
    if (!candidate) continue;
    if (path === null) path = candidate;
    else if (JSON.stringify(path) !== JSON.stringify(candidate)) return null;
  }
  return path;
};

export const caseForLabel = (
  label: string,
  decisionPath: ReadonlyArray<string | number>,
): SwitchCase => ({
  label,
  when: {
    op: 'eq',
    left: { op: 'path', path: [VALUE_ROOT, ...decisionPath] },
    right: { op: 'literal', value: label },
  } as ExpressionAst,
});

export interface CaseSyncPlan {
  cases: SwitchCase[];
  added: string[];
  removed: string[];
  kept: string[];
}

/**
 * The Switch's cases after syncing to the labels: one case per label on the
 * decision path, existing cases for still-present labels kept as they are
 * (so hand edits survive), cases for dropped labels removed.
 */
export const planCaseSync = (
  cases: readonly SwitchCase[],
  labels: readonly string[],
  decisionPath: ReadonlyArray<string | number>,
): CaseSyncPlan => {
  const byLabel = new Map(cases.map((item) => [item.label, item]));
  const labelSet = new Set(labels);
  const next: SwitchCase[] = [];
  const added: string[] = [];
  const kept: string[] = [];
  for (const label of labels) {
    const existing = byLabel.get(label);
    if (existing) {
      next.push(existing);
      kept.push(label);
    } else {
      next.push(caseForLabel(label, decisionPath));
      added.push(label);
    }
  }
  const removed = cases
    .map((item) => item.label)
    .filter((label) => !labelSet.has(label));
  return { cases: next, added, removed, kept };
};

/** One sentence for the sync button's confirmation. */
export const describeCaseSync = (plan: CaseSyncPlan): string => {
  const parts: string[] = [];
  if (plan.added.length)
    parts.push(`add ${plan.added.map((l) => `"${l}"`).join(', ')}`);
  if (plan.removed.length) {
    parts.push(`remove ${plan.removed.map((l) => `"${l}"`).join(', ')}`);
  }
  if (!parts.length) return 'The cases already match these labels.';
  const kept = plan.kept.length ? `; ${plan.kept.length} kept` : '';
  return `This will ${parts.join(' and ')}${kept}.`;
};

/** Plain-language summary for the panel header. */
export const describeClassifyPlan = (config: ClassifyConfig): string => {
  const labels = config.labels.length
    ? config.labels.map((label) => `"${label}"`).join(', ')
    : 'labels you choose';
  const fallback = config.uncertainLabel
    ? `; below ${Math.round((config.uncertainThreshold ?? 0) * 100)}% confidence it answers "${config.uncertainLabel}"`
    : '';
  return `Reads the subject and picks exactly one of ${labels}${fallback}. Connect the Decision output to a Switch to route on it, or use it as a loop's judge.`;
};
